import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tasksApi, type AssignTaskPayload, type CreateTaskPayload, type UpdateTaskPayload } from '@/api/tasks';

const TASKS_KEY            = ['me', 'tasks']         as const;
const COLLEAGUES_KEY       = ['me', 'colleagues']    as const;
const ASSIGNED_BY_ME_KEY   = ['tasks', 'assigned-by-me'] as const;

export const useTasks = () =>
  useQuery({
    queryKey: TASKS_KEY,
    queryFn:  () => tasksApi.list(),
    staleTime:       15_000,
    refetchInterval: 30_000, // auto-refresh 30s (utile si l'attributeur change le status)
  });

export const useCreateTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CreateTaskPayload) => tasksApi.create(p),
    onSuccess:  () => qc.invalidateQueries({ queryKey: TASKS_KEY }),
  });
};

export const useUpdateTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTaskPayload }) => tasksApi.update(id, payload),
    onSuccess:  () => qc.invalidateQueries({ queryKey: TASKS_KEY }),
  });
};

export const useDeleteTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksApi.remove(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: TASKS_KEY }),
  });
};

// ── Assignation hiérarchique ──────────────────────────────────────────

export const useColleagues = () =>
  useQuery({ queryKey: COLLEAGUES_KEY, queryFn: () => tasksApi.listColleagues(), staleTime: 5 * 60_000 });

export const useAssignedByMe = () =>
  useQuery({
    queryKey: ASSIGNED_BY_ME_KEY,
    queryFn:  () => tasksApi.listAssignedByMe(),
    staleTime:       10_000,
    refetchInterval: 20_000, // suivi temps réel : voir l'avancement des tâches attribuées
  });

export const useAssignTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: AssignTaskPayload) => tasksApi.assign(p),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ASSIGNED_BY_ME_KEY });
      qc.invalidateQueries({ queryKey: TASKS_KEY });
    },
  });
};
